import React, { useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushNotificationCompactRow from './Notifications/PushNotificationCompactRow';
import { getPushNotificationPresentation } from '../utils/pushNotificationPresentation';
import { formatPushNotificationTimeLabel } from './Notifications/notificationTimeFormat';
import { parseFcmPushPayload } from '../utils/fcmPushPayload';

/**
 * Compact foreground FCM toast — tap navigates via React Router (must render under Router).
 */
const PushNotificationToast = ({ toastId, title, payload, onOpen, onDismiss }) => {
  const parsed = parseFcmPushPayload(payload);
  const { accentColor } = getPushNotificationPresentation(payload);
  const timeLabel = formatPushNotificationTimeLabel({
    data: parsed.data,
    ...parsed.data,
  });

  const handleOpen = useCallback(() => {
    onDismiss?.(toastId);
    void onOpen?.(payload);
  }, [onDismiss, onOpen, payload, toastId]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-live="assertive"
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: 360,
        minWidth: 280,
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
        cursor: 'pointer',
        pointerEvents: 'auto',
        transition: 'box-shadow 0.15s ease',
        animation: 'pushToastIn 0.28s ease-out',
        '&:hover': {
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(15, 23, 42, 0.08)',
        },
        '&:focus-visible': {
          outline: `2px solid ${accentColor}`,
          outlineOffset: 2,
        },
        '@keyframes pushToastIn': {
          from: { opacity: 0, transform: 'translateX(16px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box sx={{ width: 3, flexShrink: 0, bgcolor: accentColor }} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          py: 1,
          pl: 1.25,
          pr: 0.25,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PushNotificationCompactRow
            payload={payload}
            title={parsed.title || title}
            timeLabel={timeLabel}
          />
        </Box>

        <ChevronRightIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0, mr: 0.25 }} />

        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={(event) => {
            event.stopPropagation();
            onDismiss?.(toastId);
          }}
          sx={{
            p: 0.5,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PushNotificationToast;
